import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  createTemplateVersion,
  getTemplate,
  publishTemplateVersion,
  type TemplateDetail,
  type TemplateVersionCreate,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, StatusBadge, ToolbarButton, WorkspaceHeader, secondaryButtonClass } from "@/components/ui";
import { STARTER_DEFINITION } from "@/features/templates/starter";
import { TemplatePreview } from "@/features/templates/TemplatePreview";
import { TemplatePrograms } from "@/features/templates/TemplatePrograms";
import { ScrollArea } from "@/components/ScrollArea";

const detailApi = getRouteApi("/staff/templates/$templateId");

type VersionSummary = TemplateDetail["versions"][number];

export function TemplateDetailPage() {
  const { templateId } = detailApi.useParams();
  const template = useQuery({ queryKey: ["template", templateId], queryFn: () => getTemplate(templateId) });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (template.error instanceof ApiError && [404, 422].includes(template.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Template not found" detail="No template matches this link." />
        <Link to="/templates" className="text-sm font-medium text-zinc-950 underline">
          Back to templates
        </Link>
      </section>
    );
  }
  if (!template.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Template" detail={templateId} />
        <ErrorBanner error={template.error} />
        {template.isLoading ? <EmptyState title="Loading template" detail="Fetching its versions." /> : null}
      </section>
    );
  }
  return <TemplateView template={template.data} />;
}

function TemplateView({ template }: { template: TemplateDetail }) {
  const { can } = useSession();
  const search = detailApi.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<VersionSummary | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["template", template.id] });
    await queryClient.invalidateQueries({ queryKey: ["templates"] });
  };
  const draft = useMutation({
    mutationFn: ({ body }: { body: TemplateVersionCreate; source: string }) => createTemplateVersion(template.id, body),
    onSuccess: async (created, { source }) => {
      setNotice(`Draft v${created.version_no} created from ${source}.`);
      await refresh();
    },
  });
  const publish = useMutation({
    mutationFn: (version: VersionSummary) => publishTemplateVersion(template.id, version.id),
    onSuccess: async (published) => {
      setConfirming(null);
      setNotice(`v${published.version_no} is now the current version.`);
      await refresh();
    },
    onError: async (error) => {
      // A 409 means the template changed under us (e.g. published elsewhere); refetch and drop the stale row.
      // Any other error is likely fixable (e.g. validation) so leave the row open with the error.
      if (error instanceof ApiError && error.status === 409) {
        setConfirming(null);
        await refresh();
      }
    },
  });
  const busy = draft.isPending || publish.isPending;
  const start = () => {
    setNotice(null);
    draft.reset();
    publish.reset();
  };
  const problems = publish.error instanceof ApiError ? publish.error.details : [];

  return (
    <section className="space-y-5">
      <WorkspaceHeader title={template.name} detail={`Certificate type ${template.cert_type}`} />
      <ErrorBanner error={draft.error} />
      {notice ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          {notice}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        <div className="border-b border-zinc-200 px-4 py-3">
          <h2 className="text-sm font-semibold">Versions</h2>
          <p className="mt-1 text-xs text-zinc-500">
            New batches use the current version. Drafts are copies of a version; edit a draft's layout in the designer, then
            publish it.
          </p>
        </div>
        {template.versions.length === 0 ? (
          <div className="space-y-3 p-4">
            <EmptyState
              title="No versions yet"
              detail="Start with a first draft: an A4 landscape page with the member's name centred."
              compact
            />
            {can("template.manage") ? (
              <ToolbarButton
                disabled={busy}
                onClick={() => {
                  start();
                  draft.mutate({ body: { definition: STARTER_DEFINITION }, source: "the starter layout" });
                }}
              >
                Create first draft
              </ToolbarButton>
            ) : null}
          </div>
        ) : (
          <ScrollArea label="Versions">
            <table aria-label="Versions" className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Version</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">In effect</th>
                  <th className="px-4 py-3 font-medium">Published by</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {template.versions.map((version) => (
                  <VersionRow
                    key={version.id}
                    templateId={template.id}
                    version={version}
                    current={version.id === template.current_version_id}
                    previewing={version.id === search.preview}
                    confirming={confirming?.id === version.id}
                    busy={busy}
                    publishing={publish.isPending}
                    onPreview={() =>
                      void navigate({
                        to: "/templates/$templateId",
                        params: { templateId: template.id },
                        search: (prev) => ({ ...prev, preview: version.id }),
                      })
                    }
                    onDraft={() => {
                      start();
                      draft.mutate({ body: { from_version_id: version.id }, source: `v${version.version_no}` });
                    }}
                    onStartPublish={() => {
                      start();
                      setConfirming(version);
                    }}
                    onCancelPublish={() => setConfirming(null)}
                    onPublish={() => publish.mutate(version)}
                  />
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>

      {publish.error ? (
        <div className="space-y-3 rounded-lg border border-red-200 bg-white p-4">
          <ErrorBanner error={publish.error} />
          {problems.length > 0 && publish.variables ? (
            <>
              <ScrollArea label={`Problems in v${publish.variables.version_no}`}>
              <table aria-label={`Problems in v${publish.variables.version_no}`} className="w-full text-left text-xs">
                <thead className="border-b border-zinc-200 bg-zinc-50 text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Where</th>
                    <th className="px-3 py-2 font-medium">Problem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {problems.map((problem, index) => (
                    <tr key={`${index}-${problem.field}-${problem.issue}`}>
                      <td className="px-3 py-2 font-mono text-zinc-700">{problem.field}</td>
                      <td className="px-3 py-2 text-zinc-800">{problem.issue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </ScrollArea>
              <p className="text-xs text-zinc-500">
                These can't be fixed on this screen. Edit the layout in the designer, and upload any missing font or image
                under Fonts.{" "}
                {can("template.manage") ? (
                  <Link
                    to="/templates/$templateId/versions/$versionId/edit"
                    params={{ templateId: template.id, versionId: publish.variables.id }}
                    search={{}}
                    className="font-medium text-zinc-950 underline"
                  >
                    Fix v{publish.variables.version_no} in the designer
                  </Link>
                ) : null}
              </p>
            </>
          ) : null}
        </div>
      ) : null}

      {search.preview ? (
        <TemplatePreview
          template={template}
          versionId={search.preview}
          options={{ signature: search.signature, batch: search.batch }}
          onOptions={(next) =>
            void navigate({
              to: "/templates/$templateId",
              params: { templateId: template.id },
              search: (prev) => ({ ...prev, ...next }),
            })
          }
          onClose={() => void navigate({ to: "/templates/$templateId", params: { templateId: template.id }, search: {} })}
        />
      ) : null}
      {can("program.read") ? <TemplatePrograms template={template} /> : null}
    </section>
  );
}

function VersionRow({
  templateId,
  version,
  current,
  previewing,
  confirming,
  busy,
  publishing,
  onPreview,
  onDraft,
  onStartPublish,
  onCancelPublish,
  onPublish,
}: {
  templateId: string;
  version: VersionSummary;
  current: boolean;
  previewing: boolean;
  confirming: boolean;
  busy: boolean;
  publishing: boolean;
  onPreview: () => void;
  onDraft: () => void;
  onStartPublish: () => void;
  onCancelPublish: () => void;
  onPublish: () => void;
}) {
  const { can } = useSession();
  const label = `v${version.version_no}`;
  const confirmButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmButton.current?.focus();
  }, [confirming]);

  return (
    <>
      <tr aria-current={previewing ? "true" : undefined} className={previewing ? "bg-brand-light/15 align-top" : "align-top"}>
        <td className="px-4 py-3">
          <span className="font-medium">{label}</span>
          {current ? (
            <span className="ml-2 rounded-full bg-zinc-950 px-2 py-0.5 text-xs font-medium text-white">Current</span>
          ) : null}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={version.status} />
        </td>
        <td className="px-4 py-3 text-zinc-700">
          {version.effective_from ? (
            <span>
              {formatDate(version.effective_from)} to {version.effective_to ? formatDate(version.effective_to) : "now"}
            </span>
          ) : (
            <span className="text-zinc-500">Not published</span>
          )}
        </td>
        <td className="px-4 py-3 text-zinc-700">
          <span>{version.published_by_name ?? "-"}</span>
        </td>
        <td className="px-4 py-3 text-zinc-500">{formatDate(version.created_at)}</td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-2">
            <ToolbarButton aria-label={`Preview ${label}`} onClick={onPreview}>
              Preview
            </ToolbarButton>
            {can("template.manage") && version.status === "draft" ? (
              <Link
                to="/templates/$templateId/versions/$versionId/edit"
                params={{ templateId, versionId: version.id }}
                search={{}}
                aria-label={`Edit layout of ${label}`}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition ${secondaryButtonClass}`}
              >
                Edit layout
              </Link>
            ) : null}
            {can("template.manage") ? (
              <ToolbarButton aria-label={`New draft from ${label}`} disabled={busy} onClick={onDraft}>
                New draft
              </ToolbarButton>
            ) : null}
            {can("template.publish") && version.status === "draft" ? (
              <ToolbarButton aria-label={`Publish ${label}`} disabled={busy || confirming} onClick={onStartPublish}>
                Publish
              </ToolbarButton>
            ) : null}
          </div>
        </td>
      </tr>
      {confirming ? (
        <tr>
          <td colSpan={6} className="bg-zinc-50 px-4 py-3">
            <div role="group" aria-label={`Confirm publish ${label}`} className="flex flex-wrap items-center gap-3 text-sm">
              <p className="min-w-72 flex-1 text-zinc-700">
                Publish {label}? It becomes the current version for new batches. Certificates already issued keep the
                version they were issued with.
              </p>
              <ToolbarButton tone="primary" ref={confirmButton} aria-label={`Confirm publish ${label}`} disabled={busy} onClick={onPublish}>
                {publishing ? "Publishing..." : `Publish ${label}`}
              </ToolbarButton>
              <ToolbarButton disabled={busy} onClick={onCancelPublish}>
                Cancel
              </ToolbarButton>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
